import { HealthBar } from './HealthBar';
import { HealthTimeline, type HealthPeriod } from './HealthTimeline';
import { StatusPill, type StatusLevel } from './StatusPill';
import { AssetPhoto } from './AssetPhoto';

export type AssetCardAsset = {
  id: string;
  name: string;
  klass: string;
  site: string;
  health: number;
  previousHealth?: number;
  thresholds: { warn: number; critical: number };
  severity?: { level: StatusLevel; label: string };
  rul?: { days: number; ci?: number };
  openAlerts?: number;
  lastService?: Date;
  /** Public URL of the asset photo. When omitted or missing the
   *  card falls back to an "IMG" placeholder so layout is stable
   *  whether or not the file is on disk. */
  photo?: string | null;
};

export type AssetCardProps = {
  asset: AssetCardAsset;
  periods: HealthPeriod[];
  selected?: boolean;
  loading?: boolean;
  onClick?: () => void;
  /**
   * Optional "Analyze ↗" affordance the Fleet design (S2) calls out:
   * a secondary CTA that opens Signal Analysis with this asset
   * pre-loaded. Rendered as a separate button so it doesn't trigger
   * the card's link role on click.
   */
  onAnalyze?: () => void;
};

function formatDate(d: Date): string {
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Full asset summary card for Fleet Cards view. Composes HealthBar,
 * HealthTimeline (30d), and StatusPill — all rendered identically to
 * their standalone forms so a card matches the detail surfaces exactly.
 */
export function AssetCard({
  asset,
  periods,
  selected = false,
  loading = false,
  onClick,
  onAnalyze,
}: AssetCardProps) {
  if (loading) {
    return (
      <article
        aria-busy="true"
        aria-label={`${asset.name} loading`}
        className="animate-pulse rounded-r-card border border-gray-300 bg-gray-50 p-sp-4"
      >
        <div className="h-3 w-24 rounded-r-chip bg-gray-100" />
        <div className="mt-sp-2 h-5 w-48 rounded-r-chip bg-gray-100" />
        <div className="mt-sp-4 h-4 w-full rounded-r-chip bg-gray-100" />
        <div className="mt-sp-4 w-full rounded-r-chip bg-gray-100" style={{ height: 18 }} />
      </article>
    );
  }

  // The card is keyboard-activable but renders as <article> when
  // static, or a <div role="link"> when clickable. Using
  // role="link" on <article> trips axe's aria-allowed-attr rule
  // (article's implicit landmark role can't co-host link semantics).
  // Pattern: role="link" + tabIndex + onKeyDown for Enter/Space, per
  // https://inclusive-components.design/cards/.
  function onKey(e: React.KeyboardEvent<HTMLElement>) {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  }

  const Element = onClick ? 'div' : 'article';

  return (
    <Element
      data-selected={selected || undefined}
      role={onClick ? 'link' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `${asset.name}, open detail` : undefined}
      onClick={onClick}
      onKeyDown={onKey}
      className={
        'rounded-r-card border bg-gray-50 p-sp-4 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-info ' +
        (selected
          ? 'border-gray-900 ring-2 ring-info'
          : 'border-gray-300 hover:border-gray-500') +
        (onClick ? ' cursor-pointer' : '')
      }
    >
      <div className="flex items-start justify-between gap-sp-3">
        <div className="flex min-w-0 items-start gap-sp-3">
          <AssetPhoto src={asset.photo} alt={asset.name} size="card" />
          <div className="min-w-0">
            <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              {asset.klass} · {asset.site}
            </p>
            <h3 className="mt-sp-1 truncate text-fs-statement font-medium text-gray-900">
              {asset.name}
            </h3>
            <p className="mt-sp-1 font-mono text-fs-meta text-gray-700">
              {asset.id}
            </p>
          </div>
        </div>
        {asset.severity ? (
          <StatusPill level={asset.severity.level} label={asset.severity.label} />
        ) : null}
      </div>

      <div className="mt-sp-4">
        <HealthBar
          size="row"
          value={asset.health}
          previous={asset.previousHealth}
          thresholds={asset.thresholds}
        />
      </div>

      <div className="mt-sp-4">
        <HealthTimeline periods={periods} variant="30d" />
      </div>

      <dl className="mt-sp-4 grid grid-cols-3 gap-sp-2 text-fs-meta">
        <div>
          <dt className="text-gray-700">RUL</dt>
          <dd className="font-mono text-gray-900">
            {asset.rul ? `${asset.rul.days}d` : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-gray-700">Open alerts</dt>
          <dd className="font-mono text-gray-900">{asset.openAlerts ?? 0}</dd>
        </div>
        <div>
          <dt className="text-gray-700">Last service</dt>
          <dd className="font-mono text-gray-900">
            {asset.lastService ? formatDate(asset.lastService) : '—'}
          </dd>
        </div>
      </dl>

      {onAnalyze ? (
        <div
          className="mt-sp-3 flex justify-end"
          // Don't propagate clicks from the inner button stack into
          // the outer card's role=link.
        >
          <button
            type="button"
            onClick={(e) => {
              // Stop the click from bubbling into the card's role=link
              // keydown / outer onClick handler — Analyze is a distinct
              // navigation target from Open detail.
              e.stopPropagation();
              onAnalyze();
            }}
            className="inline-flex items-center gap-sp-1 text-fs-meta font-medium text-info hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-info"
            aria-label={`Open ${asset.name} in Signal Analysis`}
          >
            Analyze ↗
          </button>
        </div>
      ) : null}
    </Element>
  );
}

